import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle2, CalendarPlus, Phone, MessageCircle, Home, AlertCircle, Star } from "lucide-react";
import { useLang } from "@/lang";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Google Ads conversion — libellé "send_to" fourni par Google Ads (Objectifs → Conversions → Configuration de la balise)
const ADS_SEND_TO = "AW-18487544205/jLpMCPO_n5MdEI2Txu9E";

const btnGold = "flex items-center justify-center gap-2 text-xs uppercase tracking-[0.15em] font-semibold bg-champagne text-white rounded-full px-6 py-3 hover:bg-champagne-bright transition-colors";
const btnOutline = "flex items-center justify-center gap-2 text-xs uppercase tracking-[0.15em] font-semibold text-zinc-700 border border-amber-600/30 rounded-full px-6 py-3 hover:border-amber-600/60 hover:text-champagne transition-colors";

const Merci = () => {
  const { t } = useLang();
  const m = t.merci || {};
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(sessionId ? "loading" : "generic");
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    document.title = "Merci — Kenny Cab";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex";
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    const poll = setInterval(async () => {
      attempts += 1;
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          clearInterval(poll);
          setBooking(res.data.booking || null);
          setState("paid");
          if (window.gtag && ADS_SEND_TO) {
            window.gtag("event", "conversion", { send_to: ADS_SEND_TO, transaction_id: sessionId });
          }
        } else if (attempts >= 15) {
          clearInterval(poll);
          setState("pending");
        }
      } catch {
        if (attempts >= 15) {
          clearInterval(poll);
          setState("pending");
        }
      }
    }, 2000);
    return () => clearInterval(poll);
  }, [sessionId]);

  const eur = (cents) => (cents / 100).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const row = (label, value, testid) => (
    <div className="flex flex-wrap justify-between gap-x-6 gap-y-1 py-3 border-b border-amber-600/10 last:border-0">
      <span className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 pt-0.5">{label}</span>
      <span data-testid={testid} className="text-sm text-ivory text-right font-medium ml-auto">{value}</span>
    </div>
  );

  return (
    <div data-testid="merci-page" className="min-h-screen flex items-center justify-center px-4 py-16">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="bg-surface border border-amber-600/20 rounded-lg p-8 sm:p-12 max-w-lg w-full shadow-[0_30px_80px_rgba(0,0,0,0.08)]"
      >
        <div className="text-center">
          {state === "loading" ? (
            <>
              <div data-testid="merci-loading" className="w-10 h-10 border-2 border-champagne border-t-transparent rounded-full animate-spin mx-auto" />
              <h1 className="font-serif text-2xl text-ivory mt-6">{m.loading}</h1>
            </>
          ) : state === "pending" ? (
            <>
              <AlertCircle size={44} strokeWidth={1.2} className="text-amber-700 mx-auto" />
              <h1 data-testid="merci-pending-title" className="font-serif text-2xl sm:text-3xl text-ivory mt-6">{m.pendingTitle}</h1>
              <p className="text-sm text-zinc-600 mt-3 leading-relaxed">{m.pendingText}</p>
            </>
          ) : (
            <>
              <CheckCircle2 size={48} strokeWidth={1.2} className="text-champagne mx-auto" />
              <h1 data-testid="merci-title" className="font-serif text-2xl sm:text-3xl text-ivory mt-6">{state === "paid" ? m.titlePaid : m.titleGeneric}</h1>
              <p className="text-sm text-zinc-600 mt-3 leading-relaxed">{state === "paid" ? m.subPaid : m.subGeneric}</p>
            </>
          )}
        </div>

        {state === "paid" && booking && (
          <div data-testid="merci-recap" className="mt-8 bg-obsidian border border-amber-600/15 rounded-md px-6 py-3">
            {row(m.reference, <span className="font-mono text-champagne tracking-widest">{booking.reference}</span>, "merci-reference")}
            {row(m.pickup, booking.pickup, "merci-pickup")}
            {row(m.destination, booking.destination, "merci-destination")}
            {row(m.datetime, `${booking.date} ${m.at} ${booking.time}`, "merci-datetime")}
            {row(m.pax, `${booking.passengers} / ${booking.luggage}`, "merci-pax")}
            {booking.payment_method === "deposit" ? (
              <>
                {row(m.depositPaid, `${eur(booking.amount_paid)} €`, "merci-deposit")}
                {row(m.balanceDue, `${eur(booking.amount_paid * 3)} €`, "merci-balance")}
              </>
            ) : (
              row(m.fullyPaid, `${eur(booking.amount_paid)} €`, "merci-total")
            )}
          </div>
        )}

        {state === "paid" && booking?.email_sent && (
          <p data-testid="merci-receipt-note" className="text-xs text-zinc-500 mt-4 text-center">{m.receiptNote}</p>
        )}

        {state === "paid" && (
          <div data-testid="merci-loyalty" className="mt-6 flex items-start gap-3 bg-champagne/10 border border-amber-600/30 rounded-md px-4 py-3">
            <Star size={16} className="text-champagne mt-0.5 shrink-0" />
            <p className="text-xs text-zinc-700 leading-relaxed">{m.loyalty}</p>
          </div>
        )}

        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          {state === "paid" && booking && (
            <a data-testid="merci-calendar-btn" href={`${API}/bookings/${booking.id}/ics`} className={btnGold}>
              <CalendarPlus size={14} /> {m.addCalendar}
            </a>
          )}
          <a data-testid="merci-call-btn" href="tel:+33651884802" className={btnOutline}>
            <Phone size={14} /> {m.call}
          </a>
          <a data-testid="merci-whatsapp-btn" href="https://wa.me/33651884802" target="_blank" rel="noopener noreferrer" className={btnOutline}>
            <MessageCircle size={14} /> {m.whatsapp}
          </a>
          <a data-testid="merci-home-btn" href="/" className={btnOutline}>
            <Home size={14} /> {m.home}
          </a>
        </div>
      </motion.div>
    </div>
  );
};

export default Merci;
